import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, InputNumber, Input, Select, Space, DatePicker, Tag, message, Statistic, Row, Col, Divider, Progress } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getSalesTeams, getSalespersons } from '../../../services/api';
import { getPerformances, upsertPerformance } from '../../../services/api';
import { getSalesTasks } from '../../../services/api';
import { getProducts } from '../../../services/api';
import { createExpense, deleteExpense, calculateExpense } from '../../../services/api';

const expenseTypeOptions = [
  { label: '差旅费', value: 'travel' },
  { label: '招待费', value: 'entertain' },
  { label: '样品费', value: 'sample' },
  { label: '投流费/佣金', value: 'traffic' },
];
const expenseTypeLabels = { travel: '差旅费', entertain: '招待费', sample: '样品费', traffic: '投流费/佣金' };

export default function PerformanceFill() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(undefined);
  const [month, setMonth] = useState(dayjs().format('YYYY-MM-01'));
  // 费用弹窗
  const [expModalOpen, setExpModalOpen] = useState(false);
  const [currentPerson, setCurrentPerson] = useState(null);
  const [calcPreview, setCalcPreview] = useState(null);
  const [products, setProducts] = useState([]);
  const [expForm] = Form.useForm();
  // 销售员选择
  const [salespersons, setSalespersons] = useState([]);
  const [salespersonId, setSalespersonId] = useState(undefined);
  // 填报表单
  const [fillForm] = Form.useForm();
  const [currentPerf, setCurrentPerf] = useState(null);
  const [personTask, setPersonTask] = useState(null);
  // 缓存编辑中的值，onBlur 时一并提交
  const [editingValues, setEditingValues] = useState({});

  // 切换团队时清理缓存，避免提交过期数据
  useEffect(() => {
    setEditingValues({});
  }, [teamId]);

  const getRowKey = (r) => `${r.salespersonId}_${r.month}`;

  const updateField = (record, field, value) => {
    const key = getRowKey(record);
    setEditingValues(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const getFieldValue = (record, field, defaultValue = 0) => {
    const key = getRowKey(record);
    return editingValues[key]?.[field] ?? defaultValue;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { month };
      if (teamId) params.teamId = teamId;
      const [pRes, tRes, prodRes] = await Promise.all([
        getPerformances(params),
        getSalesTeams(),
        getProducts({ pageSize: 200 }),
      ]);
      setData(pRes.data || []);
      setTeams(tRes.data?.list || []);
      setProducts(prodRes.data?.list || []);
    } catch (err) { message.error(err.message); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [month, teamId]);

  useEffect(() => {
    if (teamId) {
      getSalespersons({ teamId, pageSize: 200 }).then(r => setSalespersons(r.data?.list || [])).catch(() => {});
    } else {
      setSalespersons([]);
    }
    setSalespersonId(undefined);
  }, [teamId]);

  useEffect(() => {
    if (salespersonId && data.length > 0) {
      const perf = data.find(d => d.salespersonId === salespersonId);
      setCurrentPerf(perf || null);
      fillForm.setFieldsValue({
        salesAmount: perf ? Number(perf.salesAmount) : 0,
        collectedAmount: perf ? Number(perf.collectedAmount) : 0,
      });
    }
    if (salespersonId) {
      getSalesTasks({ teamId, month }).then(r => {
        const task = (r.data || []).find(t => t.salespersonId === salespersonId);
        setPersonTask(task || null);
      }).catch(() => {});
    } else {
      setPersonTask(null);
      setCurrentPerf(null);
      fillForm.resetFields();
    }
  }, [salespersonId, data]);

  const handleSavePerf = async (record) => {
    const key = getRowKey(record);
    const vals = editingValues[key] || {};
    try {
      await upsertPerformance({
        id: record.id,
        salespersonId: record.salespersonId,
        teamId: record.teamId,
        month: record.month,
        salesAmount: vals.salesAmount ?? record.salesAmount,
        collectedAmount: vals.collectedAmount ?? record.collectedAmount,
      });
      // 保存成功后清除缓存
      setEditingValues(prev => { const next = { ...prev }; delete next[key]; return next; });
      message.success('保存成功');
      fetchData();
    } catch (err) { message.error(err.message); }
  };

  const openExpModal = (person) => {
    setCurrentPerson(person);
    expForm.resetFields();
    setExpModalOpen(true);
  };

  const handleAddExpense = async () => {
    const values = await expForm.validateFields();
    try {
      await createExpense({
        salespersonId: currentPerson.salespersonId,
        teamId: currentPerson.teamId,
        month: currentPerson.month,
        expenseType: values.expenseType,
        amount: values.amount,
        productId: values.productId || null,
        sampleQty: values.sampleQty || null,
        remark: values.remark,
      });
      message.success('费用添加成功');
      setExpModalOpen(false);
      fetchData();
    } catch (err) { message.error(err.message); }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await deleteExpense(expenseId);
      fetchData();
    } catch (err) { message.error(err.message); }
  };

  const handleFillSave = async () => {
    const values = await fillForm.validateFields();
    try {
      const payload = { salespersonId, teamId, month, ...values };
      if (currentPerf?.id) payload.id = currentPerf.id;
      await upsertPerformance(payload);
      message.success('保存成功');
      setSalespersonId(undefined);
      fetchData();
    } catch (err) { message.error(err.message); }
  };

  const columns = [
    { title: '团队', dataIndex: ['team', 'name'] },
    { title: '销售人员', dataIndex: ['salesperson', 'name'] },
    { title: '月任务额', dataIndex: 'taskAmount', render: (v) => v ? `¥${Number(v).toLocaleString()}` : <Tag>-</Tag> },
    { title: '完成率', dataIndex: 'taskRate', render: (v) => v ? <Progress percent={Number(v)} size="small" /> : <Tag>-</Tag> },
    { title: '销售额', dataIndex: 'salesAmount', render: (v, r) => (
      <InputNumber size="small" style={{ width: 120 }} value={getFieldValue(r, 'salesAmount', Number(v))} min={0} precision={2}
        onChange={(val) => updateField(r, 'salesAmount', val)} onBlur={() => handleSavePerf(r)} />
    )},
    { title: '回款额', dataIndex: 'collectedAmount', render: (v, r) => (
      <InputNumber size="small" style={{ width: 120 }} value={getFieldValue(r, 'collectedAmount', Number(v))} min={0} precision={2}
        onChange={(val) => updateField(r, 'collectedAmount', val)} onBlur={() => handleSavePerf(r)} />
    )},
    { title: '费用明细', render: (_, r) => (
      <div>
        {(r.expenses || []).map(e => (
          <Tag key={e.id} closable onClose={() => handleDeleteExpense(e.id)}
            color={e.expenseType === 'sample' ? 'purple' : e.expenseType === 'travel' ? 'blue' : e.expenseType === 'entertain' ? 'orange' : 'cyan'}>
            {expenseTypeLabels[e.expenseType] || e.expenseType}: ¥{Number(e.amount).toLocaleString()}
            {e.product ? ` (${e.product.name}×${e.sampleQty})` : ''}
          </Tag>
        ))}
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => openExpModal(r)}>添加</Button>
      </div>
    )},
    { title: '费用占比', render: (_, r) => {
      if (r.expenseRate === null) return <Tag>-</Tag>;
      const rate = Number(r.expenseRate);
      return <Tag color={rate > 20 ? 'red' : rate > 10 ? 'orange' : 'green'}>{rate}%</Tag>;
    }},
  ];

  const teamSummary = data.reduce((acc, r) => {
    if (!acc[r.teamId]) acc[r.teamId] = { teamName: r.team?.name, salesAmount: 0, expenseTotal: 0 };
    acc[r.teamId].salesAmount += Number(r.salesAmount || 0);
    acc[r.teamId].expenseTotal += Number(r.expenseTotal || 0);
    return acc;
  }, {});

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <DatePicker picker="month" value={dayjs(month)} onChange={(d) => d && setMonth(d.format('YYYY-MM-01'))} />
        <Select allowClear placeholder="选择团队" style={{ width: 150 }}
          options={teams.map(t => ({ label: t.name, value: t.id }))}
          onChange={(v) => setTeamId(v)} />
        <Select allowClear placeholder="选择填报人" style={{ width: 150 }}
          disabled={!teamId}
          value={salespersonId}
          onChange={(v) => setSalespersonId(v)}
          options={salespersons.map(s => ({ label: s.name, value: s.id }))} />
        {salespersonId && (
          <Button type="primary" onClick={handleFillSave}>保存填报</Button>
        )}
      </Space>

      {salespersonId && currentPerf ? (
        <Card size="small" title={
          <span>
            填报人：{currentPerf.salesperson?.name || '-'}
            <Tag style={{ marginLeft: 8 }}>{currentPerf.team?.name}</Tag>
            <Tag>{dayjs(month).format('YYYY年M月')}</Tag>
          </span>
        }>
          <Row gutter={24}>
            <Col span={6}>
              <Form form={fillForm} layout="vertical">
                <Form.Item name="salesAmount" label="销售额 (元)" rules={[{ required: true, message: '请输入' }]}>
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="collectedAmount" label="回款额 (元)" rules={[{ required: true, message: '请输入' }]}>
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              </Form>
            </Col>
            <Col span={6}>
              <Statistic title="月任务额" value={currentPerf.taskAmount || 0} precision={2} prefix="¥" />
              <Divider />
              <Statistic title="当前完成率" value={currentPerf.taskRate || 0} suffix="%" />
              <Divider />
              <div style={{ fontWeight: 500, marginBottom: 8 }}>费用明细</div>
              {(currentPerf.expenses || []).map(e => (
                <Tag key={e.id} closable onClose={() => handleDeleteExpense(e.id)}
                  color={e.expenseType === 'sample' ? 'purple' : e.expenseType === 'travel' ? 'blue' : e.expenseType === 'entertain' ? 'orange' : 'cyan'}>
                  {expenseTypeLabels[e.expenseType] || e.expenseType}: ¥{Number(e.amount).toLocaleString()}
                  {e.product ? ` (${e.product.name}×${e.sampleQty})` : ''}
                </Tag>
              ))}
              <div style={{ marginTop: 8 }}>
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => openExpModal({
                  salespersonId, teamId, month, salesperson: currentPerf.salesperson, team: currentPerf.team,
                })}>添加费用</Button>
              </div>
            </Col>
          </Row>
        </Card>
      ) : salespersonId && !currentPerf ? (
        <Card size="small" title={`填报人：${salespersons.find(s => s.id === salespersonId)?.name || ''}`}>
          <Row gutter={24}>
            <Col span={6}>
              <Form form={fillForm} layout="vertical">
                <Form.Item name="salesAmount" label="销售额 (元)" rules={[{ required: true, message: '请输入' }]}>
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="collectedAmount" label="回款额 (元)" rules={[{ required: true, message: '请输入' }]}>
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              </Form>
            </Col>
            {personTask && (
              <Col span={6}>
                <Statistic title="月任务额" value={personTask.targetAmount || 0} precision={2} prefix="¥" />
              </Col>
            )}
          </Row>
        </Card>
      ) : null}

      {!salespersonId && (
        <>
          <Table dataSource={data} columns={columns} rowKey={(r) => `${r.salespersonId}_${r.month}`}
            loading={loading} pagination={false} expandable={{
              expandedRowRender: (r) => (
                <div style={{ padding: '8px 0' }}>
                  <Row gutter={16}>
                    <Col><Statistic title="业绩" value={r.salesAmount || 0} precision={2} prefix="¥" /></Col>
                    <Col><Statistic title="费用合计" value={r.expenseTotal || 0} precision={2} prefix="¥" /></Col>
                    <Col>
                      <Statistic title="费用占比" value={r.expenseRate !== null ? `${r.expenseRate}%` : '-'}
                        valueStyle={{ color: Number(r.expenseRate || 0) > 20 ? '#cf1322' : '#3f8600' }} />
                    </Col>
                  </Row>
                </div>
              ),
            }} />
          <Divider>团队合计</Divider>
          <Row gutter={16}>
            {Object.values(teamSummary).map(t => (
              <Col span={8} key={t.teamName}>
                <Card size="small" title={t.teamName}>
                  <Statistic title="总业绩" value={t.salesAmount} precision={2} prefix="¥" />
                  <Statistic title="总费用" value={t.expenseTotal} precision={2} prefix="¥" />
                  <Statistic title="费用占比"
                    value={t.salesAmount > 0 ? `${(t.expenseTotal / t.salesAmount * 100).toFixed(2)}%` : '-'}
                    valueStyle={{ color: t.salesAmount > 0 && (t.expenseTotal / t.salesAmount) > 0.2 ? '#cf1322' : '#3f8600' }} />
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      <Modal title="添加费用" open={expModalOpen} onOk={handleAddExpense} onCancel={() => setExpModalOpen(false)}>
        <Form form={expForm} layout="vertical">
          <Form.Item name="expenseType" label="费用类型" rules={[{ required: true }]}>
            <Select options={expenseTypeOptions} onChange={() => setCalcPreview(null)} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.expenseType !== cur.expenseType}>
            {({ getFieldValue }) => {
              const type = getFieldValue('expenseType');
              if (type === 'sample') {
                return (
                  <>
                    <Form.Item name="productId" label="产品" rules={[{ required: true }]}>
                      <Select showSearch filterOption={(input, option) => option.label.includes(input)}
                        options={products.map(p => ({ label: `¥${p.samplePrice} ${p.name} ${p.spec}`, value: p.id }))}
                        onChange={async (pid) => {
                          const qty = expForm.getFieldValue('sampleQty');
                          if (pid && qty) {
                            const res = await calculateExpense({ productId: pid, sampleQty: qty });
                            setCalcPreview(res.data);
                          } else {
                            setCalcPreview(null);
                          }
                        }} />
                    </Form.Item>
                    <Form.Item name="sampleQty" label="数量" rules={[{ required: true }]}>
                      <InputNumber style={{ width: '100%' }} min={1}
                        onChange={async (qty) => {
                          const pid = expForm.getFieldValue('productId');
                          if (pid && qty) {
                            const res = await calculateExpense({ productId: pid, sampleQty: qty });
                            setCalcPreview(res.data);
                          } else {
                            setCalcPreview(null);
                          }
                        }} />
                    </Form.Item>
                    {calcPreview && (
                      <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, padding: '8px 12px', marginBottom: 16 }}>
                        自动计算金额：<strong>¥{Number(calcPreview.amount).toLocaleString()}</strong>
                        （单价 ¥{calcPreview.samplePrice} × {expForm.getFieldValue('sampleQty')} 份）
                      </div>
                    )}
                  </>
                );
              }
              return (
                <Form.Item name="amount" label="金额(元)" rules={[{ required: true }]}>
                  <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item name="remark" label="备注"><Input /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
